import React, { useRef, useState } from 'react';
import { Upload, Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export default function ImportExport({ onImport, onExport, onSampleTemplate, onSuccess, module = 'Data', compact = false }) {
  const { canEdit } = useAuth();
  const fileRef     = useRef();
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel (.xlsx) or CSV file');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await onImport(formData);
      toast.success(res.data?.message || `${module} imported successfully`);
      if (res.data?.errors?.length) {
        toast.error(`${res.data.errors.length} rows had errors`, { duration: 5000 });
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await onExport();
      const url  = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href     = url;
      link.download = `${module.toLowerCase()}_export.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded!');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const handleSampleTemplate = async () => {
    try {
      const res  = await onSampleTemplate();
      const url  = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href     = url;
      link.download = `${module.toLowerCase()}_template.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Sample template downloaded!');
    } catch { toast.error('Template download failed'); }
  };

  const sz = compact
    ? { btn: 'px-2.5 py-1.5 text-xs gap-1.5', icon: 14 }
    : { btn: 'px-4 py-2 text-sm gap-2',        icon: 16 };

  return (
    <div className="flex flex-wrap gap-2">
      {canEdit && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFile}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className={`flex items-center font-medium rounded-lg transition-colors disabled:opacity-60 shadow-sm bg-emerald-500 hover:bg-emerald-600 text-white ${sz.btn}`}
          >
            {importing ? <Loader2 size={sz.icon} className="animate-spin" /> : <Upload size={sz.icon} />}
            {importing ? 'Importing...' : 'Import Excel'}
          </button>
        </>
      )}

      <button
        onClick={handleExport}
        disabled={exporting}
        className={`flex items-center font-medium rounded-lg transition-colors disabled:opacity-60 shadow-sm bg-primary-600 hover:bg-primary-700 text-white ${sz.btn}`}
      >
        {exporting ? <Loader2 size={sz.icon} className="animate-spin" /> : <Download size={sz.icon} />}
        {exporting ? 'Exporting...' : 'Export Excel'}
      </button>

      <button
        onClick={handleSampleTemplate}
        className={`flex items-center font-medium rounded-lg transition-colors shadow-sm bg-slate-100 hover:bg-slate-200 text-slate-700 ${sz.btn}`}
      >
        <FileSpreadsheet size={sz.icon} />
        Sample Template
      </button>
    </div>
  );
}
